import {
  ChannelThreadId,
  DraftMessageId,
  MessageAttachmentId,
  MessageId,
  SupportTicketId,
  SupportTicketId as SupportTicketIdSchema,
} from "@redotech/effect-api-shared/ids/definitions";
import type { SaveDraftInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { Attachment } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/attachment";
import type { SupportTicketStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import { useAsyncDebounce } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { messageInputShortcuts } from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { Effect, Option } from "effect";
import { KEYS, type TElement, type Value } from "platejs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useCloseSupportTicketAction,
  useDeleteDraftAction,
  useMarkInProgressSupportTicketAction,
  useSendMessageAction,
} from "../actions/use-support-ticket-actions";
import type { EditorToolbarMode } from "../components/message-editor/editor-plugins";
import { useMessageEditor } from "../components/message-editor/use-message-editor";
import { catchApiErrors } from "../db/error-handling";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { useTeamNotificationStream } from "../stream/team-notification-stream-provider";
import { usePublishDraftSupportTicket } from "./use-create-support-ticket";
import { useAiCopilotDraft, useDraft, useDraftMutations } from "./use-draft";
import { useImageUpload } from "./use-image-upload";
import { useNotifyTyping } from "./use-notify-presence";
import type { ReplyMode } from "./use-reply-mode";
import { useSendMessage } from "./use-send-message";

interface EmailRecipients {
  to: { email: string; name: string | null }[];
  cc: { email: string; name: string | null }[];
  bcc: { email: string; name: string | null }[];
}

function buildSaveDraftInput(
  base: {
    threadId: ChannelThreadId;
    inReplyToMessageId?: MessageId;
    content: string;
    plainContent?: string;
    attachments: Attachment[];
  },
  channel: MessageChannel,
  recipients?: EmailRecipients,
): SaveDraftInput {
  if (channel === "gmail" || channel === "outlook") {
    return {
      ...base,
      channel,
      channelData: {
        to: recipients?.to,
        cc: recipients?.cc,
        bcc: recipients?.bcc,
      },
    };
  }
  return { ...base, channel, channelData: null };
}

function textToPlateValue(text: string): Value {
  const paragraphs = text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) {
    return [{ type: "p", children: [{ text: "" }] } as TElement];
  }

  return paragraphs.map(
    (paragraph) => ({ type: "p", children: [{ text: paragraph }] }) as TElement,
  );
}

function getAiDraftText(draft: {
  plainContent: string | null;
  content: string;
}) {
  return draft.plainContent?.trim() || draft.content.trim();
}

interface UseMessageInputStateOptions {
  supportTicketId: SupportTicketId;
  threadId: ChannelThreadId;
  editorMode: EditorToolbarMode;
  channel?: MessageChannel;
  /** Required for email (reply to specific message), optional for chat */
  inReplyToMessageId?: MessageId;
  /** Email recipients for the reply (from the message being replied to) */
  replyRecipients?: EmailRecipients;
  onDiscardDraft?: () => void;
  supportTicketStatus?: SupportTicketStatus;
  /** Required when supportTicketStatus === "DRAFT" to publish the support ticket */
  draftPublishParams?: { customerEmail: string; subject: string };
  replyMode?: ReplyMode;
  /** When true, send handlers (including keyboard shortcuts) are no-ops. */
  sendDisabled?: boolean;
}

export function useMessageInputState({
  supportTicketId,
  threadId,
  editorMode,
  channel,
  inReplyToMessageId,
  replyRecipients,
  onDiscardDraft,
  supportTicketStatus,
  draftPublishParams,
  replyMode,
  sendDisabled = false,
}: UseMessageInputStateOptions) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalSupportTicketCollections();
  const editorId = `message-input-${threadId}-${inReplyToMessageId ?? "thread"}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const editorState = useMessageEditor(editorMode, editorId);
  const [isGeneratingAiResponse, setIsGeneratingAiResponse] = useState(false);
  const [aiSuggestedReply, setAiSuggestedReply] = useState("");
  const [aiSuggestedDraftId, setAiSuggestedDraftId] =
    useState<DraftMessageId | null>(null);
  const [ignoredAiCopilotDraftId, setIgnoredAiCopilotDraftId] =
    useState<DraftMessageId | null>(null);
  const aiTextAccumulatorRef = useRef("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [draftContent, setDraftContent] = useState<string>("");
  const hasLoadedDraft = useRef(false);
  const isLoadingDraftRef = useRef(false);

  // Chat drafts are keyed per-thread (not per-reply-target) so the
  // optimistic SENDING state survives when a new inbound message arrives
  // and changes the latest message id. inReplyToMessageId is still written
  // to the draft at save time so the backend can set message.in_reply_to_id.
  const draftLookupInReplyToMessageId =
    editorMode === "email" ? inReplyToMessageId : undefined;

  const { data: draft } = useDraft({
    supportTicketId,
    threadId,
    inReplyToMessageId: draftLookupInReplyToMessageId,
  });
  const { data: aiCopilotDraft } = useAiCopilotDraft({
    supportTicketId,
    threadId,
    inReplyToMessageId: draftLookupInReplyToMessageId,
  });
  const persistedAiSuggestedReply =
    aiCopilotDraft && aiCopilotDraft.id !== ignoredAiCopilotDraftId
      ? getAiDraftText(aiCopilotDraft)
      : "";
  const visibleAiSuggestedReply = aiSuggestedReply || persistedAiSuggestedReply;

  const {
    saveDraft,
    isSavingDraft,
    deleteDraft,
    isDeletingDraft,
    waitForPendingSave,
  } = useDraftMutations({
    supportTicketId,
    threadId,
    inReplyToMessageId: draftLookupInReplyToMessageId,
    existingDraftId: draft?.id ?? null,
    editorMode,
  });

  const { sendSupportTicketMessage, isSending: isSendingMessage } =
    useSendMessage({ supportTicketId });
  const { publishDraftSupportTicket, isPublishing } =
    usePublishDraftSupportTicket();
  const isSending = isSendingMessage || isPublishing;
  const notifyTyping = useNotifyTyping(supportTicketId);

  const getSupportTicketDraftId = useCallback(() => draft?.id, [draft]);

  const ensureDraft = useCallback(async () => {
    // Wait for any pending save to complete
    await waitForPendingSave();
    // After waiting, draft should exist
    if (!draft?.id) {
      throw new Error("Draft not found after waiting for pending save");
    }
    return draft.id;
  }, [waitForPendingSave, draft]);

  const { uploadImage } = useImageUpload({
    getDraftId: getSupportTicketDraftId,
    ensureDraft,
  });

  const handleImageSelect = useCallback(
    async (file: File) => {
      const url = await uploadImage(file);
      if (url && editorState.editor) {
        editorState.editor.tf.insertNodes(
          { type: KEYS.img, url, children: [{ text: "" }] },
          { removeEmpty: true },
        );
        editorState.editor.tf.focus();
      }
    },
    [uploadImage, editorState.editor],
  );

  const debouncedSaveDraft = useAsyncDebounce(
    useCallback(
      async (content: string, currentAttachments: Attachment[]) => {
        const plainText = editorState.getPlainText();
        const isEmpty = !plainText.trim() && currentAttachments.length === 0;
        if (isEmpty) {
          return;
        }

        await saveDraft(
          buildSaveDraftInput(
            {
              threadId,
              inReplyToMessageId: draftLookupInReplyToMessageId,
              content,
              plainContent: plainText,
              attachments: currentAttachments,
            },
            channel ?? "redoChat",
            replyRecipients,
          ),
        );
      },
      [
        draftLookupInReplyToMessageId,
        threadId,
        editorState,
        saveDraft,
        replyRecipients,
        channel,
      ],
    ),
    500,
  );

  useEffect(() => {
    if (!draft || hasLoadedDraft.current || draft.status === "SENDING") {
      return;
    }

    try {
      // Skip setValue if the editor already has this content (e.g. the user
      // typed something and the first save just created the draft with that
      // same content). Calling setValue unnecessarily causes focus loss.
      // An empty content string (seeded server-side as a placeholder for a
      // fresh draft thread) is treated as "nothing to load" — the editor's
      // default empty value stays put.
      if (draft.content && draft.content !== draftContent) {
        const parsedValue = JSON.parse(draft.content) as Value;
        isLoadingDraftRef.current = true;
        editorState.setValue(parsedValue);
        setDraftContent(draft.content);
      }
      setAttachments([...draft.attachments]);
      hasLoadedDraft.current = true;
    } catch {
      toast("Failed to load draft", { variant: "error" });
    }
  }, [draft, editorState, inReplyToMessageId, draftContent]);

  const handleEditorChange = useHandler(({ value }: { value: Value }) => {
    if (isLoadingDraftRef.current) {
      isLoadingDraftRef.current = false;
      return;
    }
    const content = JSON.stringify(value);
    if (content === draftContent) {
      return;
    }
    setDraftContent(content);
    notifyTyping();
    void debouncedSaveDraft(content, attachments);
  });

  const handleFileSelect = useHandler((files: File[]) => {
    const newAttachments: Attachment[] = files.map((file) => ({
      id: MessageAttachmentId.generate(),
      url: URL.createObjectURL(file),
      description: Option.fromNullable(file.name),
      mimeType: Option.fromNullable(file.type),
    }));
    setAttachments((prev) => {
      const updated = [...prev, ...newAttachments];
      void debouncedSaveDraft(draftContent, updated);
      return updated;
    });
  });

  const handleRemoveAttachment = useCallback(
    (index: number) => {
      setAttachments((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        void debouncedSaveDraft(draftContent, updated);
        return updated;
      });
    },
    [draftContent, debouncedSaveDraft],
  );

  const closeAction = useCloseSupportTicketAction();
  const markInProgressAction = useMarkInProgressSupportTicketAction();

  const sendAndReset = useCallback(async (): Promise<boolean> => {
    if (!editorState.getPlainText() && attachments.length === 0) {
      return false;
    }

    // Wait for any pending draft save to complete before sending
    await waitForPendingSave();

    const html = await editorState.getHtml();

    if (supportTicketStatus === "DRAFT") {
      if (!draftPublishParams?.customerEmail || !draftPublishParams?.subject) {
        toast("Customer email and subject are required to send", {
          variant: "error",
        });
        return false;
      }
      await publishDraftSupportTicket({
        supportTicketId,
        customerEmail: draftPublishParams.customerEmail,
        subject: draftPublishParams.subject,
        content: html,
        attachments,
      });
    } else {
      const sendChannel =
        replyMode === "internalNote" ? "internalNote" : channel;
      if (!sendChannel) {
        return false;
      }

      // Save one final time with latest content before sending
      const plainText = editorState.getPlainText();
      await saveDraft(
        buildSaveDraftInput(
          {
            threadId,
            inReplyToMessageId: draftLookupInReplyToMessageId,
            content: html,
            plainContent: plainText,
            attachments,
          },
          sendChannel,
          replyRecipients,
        ),
      );

      // Query database directly to get the draft ID (don't rely on collection state)
      const client = effectApi.getClient();
      const draftsResult = await effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.getSupportTicketDrafts({
            path: { id: SupportTicketIdSchema.make(supportTicketId) },
          }),
        ),
      );

      const currentDraft = draftsResult.drafts.find((d) => {
        if (d.kind !== "merchant") {
          return false;
        }
        if (String(d.threadId) !== String(threadId)) {
          return false;
        }
        if (editorMode === "email" && draftLookupInReplyToMessageId) {
          return (
            Option.isSome(d.inReplyToMessageId) &&
            String(Option.getOrThrow(d.inReplyToMessageId)) ===
              String(draftLookupInReplyToMessageId)
          );
        }
        return Option.isNone(d.inReplyToMessageId);
      });

      if (!currentDraft) {
        return false;
      }

      // Write draft to collection so sendSupportTicketMessage can update its status
      collections.drafts.utils.writeUpsert({
        ...currentDraft,
        inReplyToMessageId: Option.getOrUndefined(
          currentDraft.inReplyToMessageId,
        )?.toString(),
      });

      await sendSupportTicketMessage({
        channel: sendChannel,
        threadId,
        draftId: currentDraft.id,
      });
    }

    editorState.clearContent();
    setAttachments([]);
    setDraftContent("");
    setAiSuggestedReply("");
    setAiSuggestedDraftId(null);
    aiTextAccumulatorRef.current = "";
    hasLoadedDraft.current = true;
    onDiscardDraft?.();
    return true;
  }, [
    editorState,
    editorMode,
    attachments,
    sendSupportTicketMessage,
    threadId,
    supportTicketStatus,
    draftPublishParams,
    publishDraftSupportTicket,
    supportTicketId,
    onDiscardDraft,
    replyMode,
    channel,
    saveDraft,
    replyRecipients,
    waitForPendingSave,
    effectApi,
    collections,
    draftLookupInReplyToMessageId,
  ]);

  const isInternalNote = replyMode === "internalNote";
  // Internal notes bypass send-path blocks because they never leave Redo.
  const shouldBlockSend = sendDisabled && !isInternalNote;

  const handleSend = useHandler(async () => {
    if (shouldBlockSend) {
      return;
    }
    if (await sendAndReset()) {
      toast(isInternalNote ? "Internal note added" : "Message sent", {
        variant: "success",
      });
    }
  });

  const handleSendAndClose = useHandler(async () => {
    if (shouldBlockSend) {
      return;
    }
    if (await sendAndReset()) {
      void closeAction.execute(supportTicketId);
      toast(
        isInternalNote
          ? "Internal note added and support ticket closed"
          : "Message sent and support ticket closed",
        { variant: "success" },
      );
    }
  });

  const handleSendAndMarkInProgress = useHandler(async () => {
    if (shouldBlockSend) {
      return;
    }
    if (await sendAndReset()) {
      void markInProgressAction.execute(supportTicketId);
      toast(
        isInternalNote
          ? "Internal note added and marked in progress"
          : "Message sent and marked in progress",
        { variant: "success" },
      );
    }
  });

  const handleDeleteDraft = useHandler(async () => {
    if (!draft?.id) {
      onDiscardDraft?.();
      return;
    }

    await deleteDraft(draft.id);
    editorState.clearContent();
    setAttachments([]);
    setDraftContent("");
    setAiSuggestedReply("");
    setAiSuggestedDraftId(null);
    aiTextAccumulatorRef.current = "";
    hasLoadedDraft.current = false;
    onDiscardDraft?.();
  });

  useTeamNotificationStream("aiTextDelta", (event) => {
    if (
      event.supportTicketId !== SupportTicketId.make(supportTicketId) ||
      event.threadId !== threadId
    ) {
      return;
    }
    aiTextAccumulatorRef.current += event.textDelta;
    setAiSuggestedReply(aiTextAccumulatorRef.current);
  });

  useTeamNotificationStream("aiStatus", (event) => {
    if (
      event.supportTicketId !== SupportTicketId.make(supportTicketId) ||
      event.threadId !== threadId
    ) {
      return;
    }
    if (event.status === "complete" || event.status === "error") {
      setIsGeneratingAiResponse(false);
      void collections.drafts.utils
        .refetch()
        .finally(() => setIgnoredAiCopilotDraftId(null));
    }
  });

  const handleGenerateAiResponse = useHandler(async () => {
    if (!effectApi || !channel || isGeneratingAiResponse) {
      return;
    }
    aiTextAccumulatorRef.current = "";
    setAiSuggestedReply("");
    setIgnoredAiCopilotDraftId(aiCopilotDraft?.id ?? null);
    setAiSuggestedDraftId(null);
    setIsGeneratingAiResponse(true);
    try {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        client.aiAgent
          .generateAiResponse({
            payload: {
              supportTicketId: SupportTicketId.make(supportTicketId),
              threadId,
              channel,
              mode: "copilot",
            },
          })
          .pipe(Effect.orDie),
      );
      setAiSuggestedDraftId(result.draftId ?? null);
      await collections.drafts.utils.refetch();
      setIgnoredAiCopilotDraftId(null);
      setIsGeneratingAiResponse(false);
    } catch (error) {
      console.error("AI response generation failed:", error);
      setIgnoredAiCopilotDraftId(null);
      setIsGeneratingAiResponse(false);
    }
  });

  const recordAiCopilotDraftDecision = useHandler(
    async (draftId: DraftMessageId, decision: "accept" | "decline") => {
      const client = effectApi.getClient();
      const request = {
        path: { id: SupportTicketIdSchema.make(supportTicketId), draftId },
      };
      const updatedDraft = await effectApi.runPromise(
        catchApiErrors(
          decision === "accept"
            ? client.supportTicket.acceptAiCopilotDraft(request)
            : client.supportTicket.declineAiCopilotDraft(request),
        ),
      );
      collections.drafts.utils.writeUpsert({
        ...updatedDraft,
        inReplyToMessageId: Option.getOrUndefined(
          updatedDraft.inReplyToMessageId,
        )?.toString(),
      });
    },
  );

  const handleAcceptAiResponse = useHandler(async () => {
    const suggestedText = visibleAiSuggestedReply.trim();
    if (!suggestedText || !channel) {
      return;
    }

    const value = textToPlateValue(suggestedText);
    const content = JSON.stringify(value);
    isLoadingDraftRef.current = true;
    editorState.setValue(value);
    setDraftContent(content);
    aiTextAccumulatorRef.current = "";
    setAiSuggestedReply("");
    setIgnoredAiCopilotDraftId(
      aiSuggestedDraftId ?? aiCopilotDraft?.id ?? null,
    );
    setAiSuggestedDraftId(null);

    try {
      await saveDraft(
        buildSaveDraftInput(
          {
            threadId,
            inReplyToMessageId: draftLookupInReplyToMessageId,
            content,
            plainContent: suggestedText,
            attachments,
          },
          channel,
          replyRecipients,
        ),
      );
      const aiDraftId = aiSuggestedDraftId ?? aiCopilotDraft?.id;
      if (aiDraftId) {
        await recordAiCopilotDraftDecision(aiDraftId, "accept");
      }
    } catch {
      toast("Failed to save AI suggestion", { variant: "error" });
    }
  });

  const handleDismissAiResponse = useHandler(async () => {
    const aiDraftId = aiSuggestedDraftId ?? aiCopilotDraft?.id;
    aiTextAccumulatorRef.current = "";
    setAiSuggestedReply("");
    setIgnoredAiCopilotDraftId(aiDraftId ?? null);
    setAiSuggestedDraftId(null);
    if (aiDraftId) {
      try {
        await recordAiCopilotDraftDecision(aiDraftId, "decline");
      } catch {
        toast("Failed to dismiss AI suggestion", { variant: "error" });
      }
    }
  });

  const sendAction = useSendMessageAction(handleSend);
  const deleteSupportTicketDraftAction =
    useDeleteDraftAction(handleDeleteDraft);

  useKeyboardShortcuts({
    bindings: useMemo(
      () => [
        { shortcut: messageInputShortcuts.SEND_MESSAGE, handler: handleSend },
        {
          shortcut: messageInputShortcuts.SEND_AND_CLOSE,
          handler: handleSendAndClose,
        },
        {
          shortcut: messageInputShortcuts.SEND_AND_MARK_IN_PROGRESS,
          handler: handleSendAndMarkInProgress,
        },
        {
          shortcut: messageInputShortcuts.DELETE_DRAFT,
          handler: handleDeleteDraft,
        },
      ],
      [
        handleSend,
        handleSendAndClose,
        handleSendAndMarkInProgress,
        handleDeleteDraft,
      ],
    ),
    scope: "messageInput",
    enabled: true,
    containerRef,
  });

  return {
    containerRef,
    editorState,
    attachments,
    draft,
    aiSuggestedReply: visibleAiSuggestedReply,
    isGeneratingAiResponse,
    isSavingDraft,
    isDeletingDraft,
    isSending,
    sendAction,
    deleteDraftAction: deleteSupportTicketDraftAction,
    handleEditorChange,
    handleAcceptAiResponse,
    handleDismissAiResponse,
    handleFileSelect,
    handleGenerateAiResponse,
    handleImageSelect,
    handleRemoveAttachment,
    handleSend,
    handleSendAndClose,
    handleSendAndMarkInProgress,
    handleDeleteDraft,
  };
}
